import ReactECharts from 'echarts-for-react';

/**
 * Error boundary wrapper for ReactECharts
 * Handles disposal errors and lifecycle edge cases
 */
export class EChartsErrorBoundary extends ReactECharts {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this._isDisposing = false;
  }

  componentDidMount() {
    this._isMounted = true;
    this._isDisposing = false;
    try {
      super.componentDidMount();

      // Patch the chart instance to handle null element access
      const instance = this.getEchartsInstance();
      if (instance && instance.getDom) {
        const originalGetDom = instance.getDom.bind(instance);
        instance.getDom = () => {
          try {
            const dom = originalGetDom();
            // Return null if element is detached or being disposed
            if (!dom || this._isDisposing || !document.contains(dom)) {
              return null;
            }
            return dom;
          } catch (e) {
            return null;
          }
        };
      }
    } catch (error) {
      console.debug('ECharts mount handled:', error.message);
    }
  }

  componentWillUnmount() {
    this._isDisposing = true;
    this._isMounted = false;

    try {
      // Get the instance before disposal
      const instance = this.getEchartsInstance();

      if (instance) {
        // Clear all event listeners to prevent further updates
        try {
          instance.off();
        } catch (e) {
          console.debug('ECharts clear events handled:', e.message);
        }

        // Stop all animations first
        if (typeof instance.setOption === 'function') {
          try {
            instance.setOption({ animation: false }, { silent: true });
          } catch (e) {
            console.debug('ECharts stop animation handled:', e.message);
          }
        }

        // Dispose the instance
        if (typeof instance.isDisposed === 'function' && !instance.isDisposed()) {
          instance.dispose();
        }
      }

      // Clear the reference
      if (this.ele) {
        this.ele.echarts_instance = null;
      }
    } catch (error) {
      // Silently catch disposal errors
      console.debug('ECharts disposal handled:', error.message);
    }

    // Note: We don't call super.componentWillUnmount() because the parent class
    // will try to dispose observers that may already be cleaned up, causing errors.
    // Our early disposal above prevents those issues.
  }

  componentDidUpdate(prevProps) {
    if (this._isDisposing || !this._isMounted) return;

    try {
      // Check if DOM element still exists before updating
      if (this.ele && document.contains(this.ele)) {
        super.componentDidUpdate(prevProps);
      }
    } catch (error) {
      console.debug('ECharts update handled:', error.message);
    }
  }

  getEchartsInstance = () => {
    if (!this._isMounted || this._isDisposing) return null;
    try {
      return super.getEchartsInstance();
    } catch (error) {
      console.debug('ECharts getInstance handled:', error.message);
      return null;
    }
  };

  resize = () => {
    if (!this._isMounted || this._isDisposing) return;
    try {
      const instance = this.getEchartsInstance();
      if (instance && typeof instance.isDisposed === 'function' && !instance.isDisposed()) {
        instance.resize();
      }
    } catch (error) {
      // Suppress resize errors when instance is null/disposed
      console.debug('ECharts resize handled:', error.message);
    }
  };
}
